import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Upload, Download, FileDown, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import jsPDF from "jspdf";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * RebrandImageTab
 *
 * Upload a marketing poster (with a dark contact band at the bottom) → the
 * system suggests a horizontal cut line at the top of the band → user can
 * fine-tune with a slider → everything below the cut line is replaced with
 * the store's own contact strip (black band, white text). Live preview is
 * rendered on an HTML canvas; export is a PNG (canvas.toDataURL) and a
 * PDF (jsPDF wrapping the same canvas image at A4 size).
 *
 * The auto-detect step is the only server call — it returns the suggested
 * y-coordinate. Everything else stays client-side for instant feedback.
 */
export default function RebrandImageTab() {
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const imgElRef = useRef(null);     // HTMLImageElement of the uploaded image

  const [imgMeta, setImgMeta] = useState(null);   // {width, height, suggested_cut_y}
  const [cutY, setCutY] = useState(0);             // current cut position (px in image space)
  const [bandHeight, setBandHeight] = useState(140); // band height in image px
  const [analyzing, setAnalyzing] = useState(false);

  // Contact details — pre-filled from /settings/company, then editable per image.
  const [details, setDetails] = useState({
    storeName: "",
    phone: "",
    email: "",
    address: "",
  });

  // Pull company settings once on mount, and again when user clicks "Reset to settings"
  const loadCompanyDefaults = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/company`);
      const c = res.data || {};
      const line2 = [c.address_line1, c.address_line2, c.city].filter(Boolean).join(", ");
      setDetails({
        storeName: c.company_name || c.name || "BOSVELD BOU & HARDEWARE",
        phone: c.phone || "",
        email: c.email || "",
        address: line2,
      });
    } catch {
      // Quietly leave fields blank — user can type their own.
    }
  }, []);

  useEffect(() => { loadCompanyDefaults(); }, [loadCompanyDefaults]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG / JPG)");
      return;
    }
    setAnalyzing(true);

    // Load the image element so we can draw onto canvas later
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      imgElRef.current = img;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/image-rebrand/analyze`, fd);
        setImgMeta(res.data);
        setCutY(res.data.suggested_cut_y);
        // Default band height = ~12 % of original image height (matches typical poster ratio)
        setBandHeight(Math.max(80, Math.round(res.data.height * 0.12)));
        if (!res.data.had_dark_band) {
          toast.info("No dark contact band detected — drag the slider to set the cut line manually.");
        }
      } catch {
        toast.error("Couldn't analyze the image — drag the slider to set the cut line manually.");
        setImgMeta({ width: img.width, height: img.height, suggested_cut_y: Math.round(img.height * 0.85) });
        setCutY(Math.round(img.height * 0.85));
        setBandHeight(Math.max(80, Math.round(img.height * 0.12)));
      } finally {
        setAnalyzing(false);
      }
    };
    img.onerror = () => { setAnalyzing(false); toast.error("Couldn't load the image"); };
    img.src = url;
  };

  // ---- Canvas rendering ----
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgElRef.current;
    if (!canvas || !img || !imgMeta) return;

    // Final canvas = original image, but everything below `cutY` is the new band.
    canvas.width = imgMeta.width;
    canvas.height = cutY + bandHeight;

    const ctx = canvas.getContext("2d");
    // 1. paint the top portion of the original image
    ctx.drawImage(img, 0, 0, imgMeta.width, cutY, 0, 0, imgMeta.width, cutY);

    // 2. draw the new black contact band
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, cutY, imgMeta.width, bandHeight);

    // 3. write the contact details inside the band, centered
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Lines to render — skip blanks so we don't waste vertical space
    const lines = [
      details.storeName,
      details.phone,
      details.email,
      details.address,
    ].filter((s) => (s || "").trim().length > 0);

    if (lines.length) {
      const padding = Math.round(bandHeight * 0.15);
      const innerH = bandHeight - padding * 2;
      const lineHeight = innerH / lines.length;
      // Largest line (store name) gets ~80 % of lineHeight
      const baseSize = Math.max(14, Math.min(lineHeight * 0.65, imgMeta.width * 0.03));

      lines.forEach((text, i) => {
        const isHeadline = i === 0;
        const size = Math.round(baseSize * (isHeadline ? 1.25 : 1));
        ctx.font = `${isHeadline ? "bold " : ""}${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
        const y = cutY + padding + lineHeight * (i + 0.5);
        ctx.fillText(text, imgMeta.width / 2, y);
      });
    }
  }, [imgMeta, cutY, bandHeight, details]);

  useEffect(() => { renderCanvas(); }, [renderCanvas]);

  // ---- Exports ----
  const downloadPng = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgMeta) return;
    const link = document.createElement("a");
    link.download = `promo-${Date.now()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("PNG downloaded");
  };

  const downloadPdf = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgMeta) return;
    // Fit the canvas onto an A4 portrait page with a 10 mm margin.
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageW = 210, pageH = 297, margin = 10;
    const maxW = pageW - margin * 2;
    const maxH = pageH - margin * 2;
    const ratio = canvas.width / canvas.height;
    let drawW = maxW, drawH = maxW / ratio;
    if (drawH > maxH) { drawH = maxH; drawW = maxH * ratio; }
    const x = (pageW - drawW) / 2;
    const y = (pageH - drawH) / 2;
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, drawW, drawH);
    pdf.save(`promo-${Date.now()}.pdf`);
    toast.success("PDF downloaded");
  };

  // Visual preview width — keep at most 720px tall to avoid eating the page
  const previewMaxWidth = 720;

  return (
    <div className="space-y-4">
      {!imgMeta && (
        <Card data-testid="rebrand-uploader">
          <CardContent className="p-8 text-center space-y-3">
            <ImageIcon className="h-10 w-10 mx-auto text-gray-400" />
            <h3 className="text-base font-semibold text-gray-800">
              Upload a marketing image with someone else's contact band
            </h3>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">
              Auto-detects the dark contact band at the bottom and lets you replace it
              with your store's details. Export as PNG or A4 PDF.
            </p>
            <Button onClick={() => fileInputRef.current?.click()} disabled={analyzing} data-testid="rebrand-upload-btn">
              {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Choose image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="rebrand-file-input"
            />
          </CardContent>
        </Card>
      )}

      {imgMeta && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* === Controls === */}
          <Card className="lg:col-span-1">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Re-Brand controls</h3>
                <Button size="sm" variant="ghost" onClick={() => { setImgMeta(null); imgElRef.current = null; }} data-testid="rebrand-reset-btn">
                  Choose another
                </Button>
              </div>

              <div>
                <Label className="text-xs">Cut line (Y position) — {cutY}px of {imgMeta.height}px</Label>
                <Slider
                  value={[cutY]}
                  onValueChange={(v) => setCutY(v[0])}
                  min={Math.round(imgMeta.height * 0.3)}
                  max={imgMeta.height - 20}
                  step={1}
                  className="mt-2"
                  data-testid="rebrand-cut-slider"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Everything below this line gets replaced. Auto-detected at {imgMeta.suggested_cut_y}px.
                </p>
              </div>

              <div>
                <Label className="text-xs">New band height — {bandHeight}px</Label>
                <Slider
                  value={[bandHeight]}
                  onValueChange={(v) => setBandHeight(v[0])}
                  min={60}
                  max={Math.round(imgMeta.height * 0.3)}
                  step={2}
                  className="mt-2"
                  data-testid="rebrand-band-slider"
                />
              </div>

              <div className="pt-2 border-t space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Contact details</Label>
                  <Button size="sm" variant="ghost" onClick={loadCompanyDefaults} data-testid="rebrand-reset-contact-btn">
                    <RefreshCw className="h-3 w-3 mr-1" /> From Settings
                  </Button>
                </div>
                <Input
                  placeholder="Store name"
                  value={details.storeName}
                  onChange={(e) => setDetails((p) => ({ ...p, storeName: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="rebrand-store-name-input"
                />
                <Input
                  placeholder="Phone"
                  value={details.phone}
                  onChange={(e) => setDetails((p) => ({ ...p, phone: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="rebrand-phone-input"
                />
                <Input
                  placeholder="Email"
                  value={details.email}
                  onChange={(e) => setDetails((p) => ({ ...p, email: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="rebrand-email-input"
                />
                <Textarea
                  placeholder="Address (one line)"
                  value={details.address}
                  onChange={(e) => setDetails((p) => ({ ...p, address: e.target.value }))}
                  className="text-sm min-h-[44px]"
                  rows={2}
                  data-testid="rebrand-address-input"
                />
              </div>

              <div className="pt-2 border-t flex gap-2">
                <Button onClick={downloadPng} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="rebrand-download-png-btn">
                  <Download className="h-4 w-4 mr-1" /> PNG
                </Button>
                <Button onClick={downloadPdf} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="rebrand-download-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" /> A4 PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* === Preview === */}
          <Card className="lg:col-span-2">
            <CardContent className="p-3">
              <div className="text-xs text-gray-500 mb-2">
                Live preview · exported at full {imgMeta.width}×{cutY + bandHeight}px resolution
              </div>
              <div className="bg-gray-100 border rounded overflow-auto" style={{ maxHeight: 780 }}>
                <canvas
                  ref={canvasRef}
                  style={{ width: "100%", maxWidth: previewMaxWidth, height: "auto", display: "block", margin: "0 auto" }}
                  data-testid="rebrand-canvas"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
